"use client";
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Modal,
  Stack,
  Fade,
  CircularProgress,
  Button,
  Tooltip,
  Chip,
  useTheme,
  useMediaQuery
} from "@mui/material";
import {
  QrCodeScanner as QrCodeScannerIcon,
  SwitchCamera as SwitchCameraIcon,
  Close as CloseIcon,
  PhotoCamera as PhotoCameraIcon,
  CameraAlt as CameraAltIcon
} from "@mui/icons-material";
import { Scanner } from "@yudiel/react-qr-scanner";
import { useDispatch, useSelector } from "react-redux";
import {
  generateAccessToken,
  getSearchResultsForMealItems
} from "../../features/level3/level3Slice";
import { selectAccessToken } from "../../features/level3/level3Selectors";
import { useCurrentUser } from "./UserContext";

const QrScannerModal = ({
  open,
  setOpen,
  setFavoriteFood,
  setOpenMenu,
  setOpenSnackbar,
  setSnackbarMessage,
  setSelectedOptionData,
  setSearchQuery
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();

  const existingToken = useSelector(selectAccessToken);

  const [accessToken, setAccessToken] = useState("");
  const [result, setResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cameraId, setCameraId] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadingCameras, setLoadingCameras] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setResult(null);
    setIsProcessing(false);
  };

  useEffect(() => {
    if (!existingToken && open) {
      dispatch(generateAccessToken()).then(({ payload }) => {
        setAccessToken(payload.access_token);
      });
    } else {
      setAccessToken(existingToken);
    }

    if (open) {
      setFavoriteFood("");
      setResult(null);
      setIsProcessing(false);
      loadCameras(); // 👈 this ensures back camera loads first every time
    }
  }, [open]);

  const loadCameras = async () => {
    try {
      setLoadingCameras(true);
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((d) => d.kind === "videoinput");

      if (videoDevices.length === 0) {
        setSnackbarMessage("No camera found. Please check device permissions.");
        setOpenSnackbar(true);
        return;
      }
      // 🧠 Filter for rear/environment cameras only
      const rearCameras = videoDevices.filter((device) => {
        const label = device.label.toLowerCase();
        return (
          label.includes("back") ||
          label.includes("rear") ||
          label.includes("environment") ||
          label.includes("camera2 0") // Common identifier for main camera on Samsung
        );
      });
      const sortedRearCameras = rearCameras.sort((a, b) => {
        const labelA = a.label.toLowerCase();
        const labelB = b.label.toLowerCase();
        if (labelA < labelB) {
          return -1; // a comes before b
        }
        if (labelA > labelB) {
          return 1; // b comes before a
        }
        return 0; // a and b are equal
      });

      // 🧹 Exclude macro, ultra-wide, depth, tele lenses - MORE COMPREHENSIVE
      const mainRear = sortedRearCameras.find((device) => {
        const label = device.label.toLowerCase();
        return (
          !label.includes("macro") &&
          !label.includes("front") &&
          !label.includes("ultra") &&
          !label.includes("ultrawide") &&
          !label.includes("ultra-wide") &&
          !label.includes("wide") && // Add this
          !label.includes("depth") &&
          !label.includes("tele") &&
          !label.includes("telephoto") &&
          !label.includes("zoom") &&
          (label.includes("main") ||
            label.includes("camera2 0") ||
            label.includes("back 0") ||
            !label.match(/back \d+/)) // Exclude numbered back cameras except "back 0"
        );
      });

      // ✅ Pick the main rear camera or fallback to first available
      const preferredCamera = mainRear || rearCameras[0] || videoDevices[0];

      setCameras([preferredCamera]);
      setCameraId(preferredCamera?.deviceId || null);

    } catch (err) {
      console.error("Camera listing error:", err);
      setSnackbarMessage("Unable to access camera. Please check permissions.");
      setOpenSnackbar(true);
    } finally {
      setLoadingCameras(false);
    }
  };

  const handleScan = async (data) => {
    if (!data || isProcessing) return;
    const barcodeValue = data[0]?.rawValue || data.rawValue || data;
    if (!barcodeValue) return;

    setResult(barcodeValue);
    setIsProcessing(true);

    try {
      const { payload } = await dispatch(
        getSearchResultsForMealItems({
          barcode: barcodeValue,
          token: accessToken,
          userId: currentUser?.userId
        })
      );

      if (payload?.results?.length) {
        const item = payload.results[0];
        const data = {
          ...item,
          name: `${item.description}${
            item.brand_name ? ` (${item.brand_name})` : ""
          } `,
          id: item.serving.serving_id
        };
        setSelectedOptionData(data);
        setSearchQuery(data?.name);
      } else {
        setSnackbarMessage("No results found for this barcode.");
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error("Processing error:", error);
      setSnackbarMessage("Error processing barcode.");
      setOpenSnackbar(true);
    } finally {
      setIsProcessing(false);
      setTimeout(() => {
        setOpen(false);
      }, 500);
    }
  };

  const handleError = (err) => {
    console.error("QR Scanner Error:", err);
    setSnackbarMessage("Camera error: please check permissions or refresh.");
    setOpenSnackbar(true);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      disableAutoFocus
      disableEnforceFocus
      closeAfterTransition
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2
      }}
    >
      <Fade in={open}>
        <Paper
          elevation={24}
          sx={{
            width: "100%",
            maxWidth: "600px",
            maxHeight: "90vh",
            overflow: "hidden",
            borderRadius: 4,
            background: "linear-gradient(135deg, #1e1e1e, #2a2a2a)",
            color: "white",
            position: "relative"
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid rgba(255,255,255,0.1)"
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <QrCodeScannerIcon />
              <Typography variant="h6">Scan Barcode</Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              {/* {cameras.length > 1 && (
                <Tooltip title="Switch Camera">
                  <IconButton
                    onClick={switchCamera}
                    sx={{
                      color: "white",
                      background: "rgba(255,255,255,0.1)",
                      "&:hover": { background: "rgba(255,255,255,0.2)" }
                    }}
                  >
                    <SwitchCameraIcon />
                  </IconButton>
                </Tooltip>
              )} */}
              <IconButton
                onClick={handleClose}
                sx={{
                  color: "white",
                  background: "rgba(255,255,255,0.1)",
                  "&:hover": { background: "rgba(255,255,255,0.2)" }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Scanner area */}
          <Box sx={{ position: "relative", width: "100%", height: 400 }}>
            {loadingCameras ? (
              <Box
                sx={{
                  height: 400,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <CircularProgress sx={{ color: theme.palette.primary.main }} />
              </Box>
            ) : isProcessing ? (
              <Box
                sx={{
                  height: 400,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <CircularProgress sx={{ color: theme.palette.primary.main }} />
                <Typography sx={{ mt: 2 }}>Processing barcode...</Typography>
              </Box>
            ) : (
              <Scanner
                onScan={handleScan}
                onError={handleError}
                components={{
                  torch: true,
                  zoom: true
                }}
                constraints={{
                  deviceId: cameraId ? { exact: cameraId } : undefined,
                  facingMode: "environment" // fallback for iOS Safari
                }}
                formats={[
                  "code_128",
                  "ean_13",
                  "ean_8",
                  "code_39",
                  "upc_a",
                  "upc_e",
                  "qr_code"
                ]}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover"
                }}
              />
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 2,
              borderTop: "1px solid rgba(255,255,255,0.1)",
              textAlign: "center"
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.6)" }}
            >
              Position barcode inside the frame. Tap to focus camera.
            </Typography>
          </Box>
        </Paper>
      </Fade>
    </Modal>
  );
};

export default QrScannerModal;
